import { useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import cn from 'classnames'
import { Swiper, SwiperSlide } from 'swiper/react'
// Components
import Button from '@/components/newButton/Button'
// Utils
import useMediaQuery from '@/utils/publicApi'
import { getBreedImage } from '@/components/home/lib/utils'
import styles from './RelatedBreeds.module.scss'

import { useHasJS } from '@/hooks/useHasJavascript'

type Breed = {
  name: string
  slug: string
}

type BreedCardProps = {
  breed: Breed
  isLanding?: boolean
}

const BreedCard = ({ breed, isLanding }: BreedCardProps) => {
  const link = isLanding
    ? `/puppies-for-sale/${breed?.slug}`
    : `/search?breed=${breed?.slug}`

  return (
    <div className={styles.breed}>
      <Link href={link}>
        <Image
          src={
            getBreedImage(breed)
              ? `${process.env.NEXT_PUBLIC_MEDIA_BUCKET}${getBreedImage(breed)}`
              : '/placeholder-breed.svg'
          }
          role="img"
          aria-label={`${breed?.name} sitting and posing`}
          alt={`${breed?.name} sitting and posing`}
          width={165}
          height={165}
          loading="lazy"
        />
      </Link>

      <Link href={link}>
        <p>{breed?.name} </p>
      </Link>
    </div>
  )
}

type RelatedBreedsProps = {
  relatedBreeds: Breed[]
  breed: Breed
  isLanding?: boolean
}

export const RelatedBreeds = ({
  relatedBreeds: _relatedBreeds,
  breed,
  isLanding,
}: RelatedBreedsProps) => {
  const isMobile = useMediaQuery('(max-width:600px)')
  const [showAll, setShowAll] = useState(false)
  const relatedBreeds = showAll ? _relatedBreeds : _relatedBreeds?.slice(0, 5)
  const hasJS = useHasJS()

  return (
    <div className={styles.relatedBreedsContainer}>
      <h2>Related Breeds to {breed.name}</h2>

      {hasJS && (
        <>
          {isMobile ? (
            <div className={cn(styles.breeds, styles.mobile)}>
              <Swiper
                grabCursor
                speed={1000}
                effect="slide"
                initialSlide={0}
                slidesPerView={2}
                updateOnWindowResize
                autoplay={{
                  disableOnInteraction: true,
                }}
              >
                {relatedBreeds?.map((b, i) => (
                  <SwiperSlide key={i}>
                    <BreedCard breed={b} isLanding={isLanding} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            <div className={cn(styles.breeds, styles.desktop)}>
              {relatedBreeds?.map((b, i) => (
                <BreedCard key={i} breed={b} isLanding={isLanding} />
              ))}
            </div>
          )}

          {!isMobile && (
            <div className={styles.buttonContainer}>
              <Button
                title={showAll ? 'View less breeds' : 'View more breeds'}
                theme={['black', 'medium']}
                rightImage={
                  showAll
                    ? '/search/arrow-top-black.svg'
                    : '/search/arrow-down-black.svg'
                }
                altImg={showAll ? 'Black up arrow' : 'Black down arrow'}
                onClick={() => setShowAll((curr) => !curr)}
              />
            </div>
          )}
        </>
      )}

      <noscript>
        <div>
          {_relatedBreeds?.map((b, i) => (
            <div key={i}>
              <a href={`/puppies-for-sale/${b?.slug}`}>{b?.name}</a>
            </div>
          ))}
        </div>
      </noscript>
    </div>
  )
}

export default RelatedBreeds
